import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Send, Loader2 } from "lucide-react";
import { SmsDialog } from "../admin/SmsDialog";
import { firstName } from "../admin/leadUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "bg-[#0a0a0a] border border-zinc-800 text-white rounded-sm px-2.5 h-10 text-sm w-full";

export const EstimateForm = ({ job, existing, onSaved }) => {
  const [items, setItems] = useState(existing?.line_items || [{ description: "", qty: 1, price: 0 }]);
  const [notes, setNotes] = useState(existing?.notes || "");
  const [saving, setSaving] = useState(false);
  const [smsEstimate, setSmsEstimate] = useState(null);

  const total = items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.price) || 0), 0);
  const setItem = (idx, k, v) => setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [k]: v } : it)));

  const save = async (thenSend = false) => {
    const valid = items.filter((i) => i.description.trim());
    if (!valid.length) return toast.error("Add at least one line item.");
    setSaving(true);
    try {
      let est;
      if (existing?.estimate_id) {
        const res = await axios.patch(`${API}/estimates/${existing.estimate_id}`, { line_items: valid, notes });
        est = res.data;
      } else {
        const res = await axios.post(`${API}/estimates`, {
          appointment_id: job.appointment_id, customer_id: job.customer_id || null,
          lead_id: job.lead_id || null, name: job.name, phone: job.phone,
          vehicle: job.vehicle_info ? `${job.vehicle_info.year || ""} ${job.vehicle_info.make || ""} ${job.vehicle_info.model || ""}`.trim() : null,
          line_items: valid, notes,
        });
        est = res.data;
      }
      if (thenSend) {
        await axios.post(`${API}/estimates/${est.estimate_id}/send`);
        est = { ...est, status: "Sent" };
        setSmsEstimate(est);
      } else {
        toast.success("Estimate saved as draft");
      }
      onSaved(est);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save estimate");
    } finally {
      setSaving(false);
    }
  };

  const link = smsEstimate ? `${window.location.origin}/estimate/${smsEstimate.token}` : "";

  return (
    <div className="space-y-3" data-testid="estimate-form">
      {items.map((it, idx) => (
        <div key={idx} className="flex gap-2">
          <input data-testid={`estimate-item-desc-${idx}`} className={inputCls} placeholder="Description" value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)} />
          <input data-testid={`estimate-item-qty-${idx}`} type="number" className={`${inputCls} !w-16`} value={it.qty} onChange={(e) => setItem(idx, "qty", e.target.value)} />
          <input data-testid={`estimate-item-price-${idx}`} type="number" step="0.01" className={`${inputCls} !w-24`} placeholder="$" value={it.price} onChange={(e) => setItem(idx, "price", e.target.value)} />
          <button type="button" onClick={() => setItems((p) => p.filter((_, i) => i !== idx))} className="text-zinc-600 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <button type="button" data-testid="estimate-add-item" onClick={() => setItems((p) => [...p, { description: "", qty: 1, price: 0 }])} className="inline-flex items-center gap-1 text-blue-400 text-xs font-semibold">
        <Plus className="h-3.5 w-3.5" /> Add line item
      </button>
      <textarea data-testid="estimate-notes" className="w-full bg-[#0a0a0a] border border-zinc-800 text-white rounded-sm p-2.5 text-sm h-16" placeholder="Estimate notes..." value={notes} onChange={(e) => setNotes(e.target.value)} />
      <div className="flex items-center justify-between">
        <div className="text-white font-heading font-bold" data-testid="estimate-total">Total: ${total.toFixed(2)}</div>
        <div className="flex gap-2">
          <button data-testid="estimate-save-draft" onClick={() => save(false)} disabled={saving} className="border border-zinc-700 text-white rounded-sm px-4 py-2 text-xs font-semibold disabled:opacity-60">
            Save Draft
          </button>
          <button data-testid="estimate-send" onClick={() => save(true)} disabled={saving} className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-4 py-2 text-xs font-semibold disabled:opacity-60">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Save &amp; Send for Signature
          </button>
        </div>
      </div>
      {smsEstimate && (
        <SmsDialog
          key={smsEstimate.estimate_id}
          open
          onOpenChange={(v) => !v && setSmsEstimate(null)}
          phone={job.phone}
          title="Text Estimate to Customer"
          initialMessage={`Hi ${firstName(job.name)}, your estimate from Mason's Mobile Mechanic is ready ($${Number(smsEstimate.total).toFixed(2)}). Review, approve & sign here: ${link} — or reply with any questions.`}
          onLogged={async (message) => {
            await axios.post(`${API}/tech/jobs/${job.appointment_id}/log-sms`, { message, channel: "sms" }).catch(() => {});
            toast.success("Estimate sent — awaiting customer signature");
          }}
        />
      )}
    </div>
  );
};
